/**
 * 3D Viewer: one setting, with a real effect.
 *
 * Point size, and nothing else. It is here because a sparse cloud of forty
 * thousand points and one of four hundred thousand cannot be read well at the
 * same point size - small, a thin reconstruction looks like dust; large, a
 * dense one becomes a solid blob that hides its own structure. The viewer
 * cannot choose for both, so the reader does.
 *
 * The change reaches an already-mounted viewer through a same-document event,
 * so a viewer open in another route redraws without a reload. That is what
 * makes this a setting rather than a value read once at mount.
 *
 * Nothing else in the viewer is offered. Background colour follows the theme,
 * the camera resets with its own button, and a field-of-view slider would be a
 * knob with no question behind it.
 */

import { useState } from "react";
import { Metric, Section } from "@/components/product";
import {
  POINT_SIZES,
  readPointSize,
  writePointSize,
  type PointSize,
} from "@/features/reconstruction/viewerPreferences";

const POINT_SIZE_LABELS = {
  small: "Küçük",
  medium: "Orta",
  large: "Büyük",
} as const satisfies Record<PointSize, string>;

export function ViewerPanel() {
  return (
    <>
      <PointSizeSetting />
      <EngineFacts />
    </>
  );
}

function PointSizeSetting() {
  const [size, setSize] = useState<PointSize>(() => readPointSize());

  return (
    <Section title="3D Görüntüleyici">
      {/*
        A radio group for the same reason appearance is one: three mutually
        exclusive states, applied immediately, nothing to save.
      */}
      {/*
        The same preference the viewer itself now offers, not a second one.
        Said here so nobody wonders which of the two is authoritative.
      */}
      <p className="muted small">
        Aynı ayarı nokta bulutunu görüntülerken doğrudan görüntüleyicinin
        üzerinden de değiştirebilirsiniz.
      </p>
      <div className="appearance" role="radiogroup" aria-label="Nokta boyutu">
        {POINT_SIZES.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={size === option}
            className={`appearance__option${size === option ? " appearance__option--on" : ""}`}
            onClick={() => {
              setSize(option);
              writePointSize(option);
            }}
          >
            {POINT_SIZE_LABELS[option]}
          </button>
        ))}
      </div>
      <p className="muted small settings__hint">{"Seyrek nokta bulutu ekranda hemen bu boyutta çizilir. Yoğun bir bulut küçük, seyrek bir bulut büyük noktalarla daha okunaklıdır."}</p>
      <p className="muted small settings__hint">{"Bu tercih hesabınıza değil, bu tarayıcıya kaydedilir: aynı kişi 4K bir ekranda ve bir telefonda farklı bir boyut ister."}</p>
    </Section>
  );
}

/**
 * What this installation can put in front of the viewer.
 *
 * Not reconstruction settings - there are none to offer, and low-level engine
 * parameters are not a user's business. It is the bound on what the viewer can
 * ever show: a sparse point cloud, because dense, mesh and texture have no
 * execution path on this server's COLMAP build. Saying so here means the
 * absence of a "textured mesh" view is explained rather than looking broken.
 */
function EngineFacts() {
  return (
    <Section title="Rekonstrüksiyon">
      <Metric label="Motor" value="COLMAP" />
      <Metric
        label="Yoğun 3D / mesh"
        value="Bu sunucuda kullanılamıyor"
        tone="neutral"
      />
      <p className="muted small settings__hint">{"Rekonstrüksiyon parametreleri sunucu tarafında tanımlıdır ve bu ekrandan değiştirilmez; düşük seviyeli ayarlar kullanıcıya açılmaz."}</p>
    </Section>
  );
}
